import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ContentChild,
  Directive,
  ElementRef,
  input,
  Input,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { HxBaseControl } from '../../base/hx-base-control';
import { HxIconButton } from '../icon-button/icon-button';
import { HxDatePicker } from './date-picker';

/**
 * Directive for custom icon in datepicker toggle.
 *
 * @example
 *   ```html
 *   <hx-datepicker-toggle hxIconSuffix [for]="picker">
 *     <hx-icon hxDatepickerToggleIcon svgIcon="calendar-check"></hx-icon>
 *   </hx-datepicker-toggle>
 *   ```;
 */
@Directive({
  selector: '[hxDatepickerToggleIcon]',
  standalone: true,
})
export class HxDatepickerToggleIcon {}

/**
 * Button that opens/closes a datepicker calendar. Typically used as a suffix in a form field.
 *
 * @example
 *   ```html
 *   <hx-form-group>
 *     <hx-label [description]="'Birth Date'"></hx-label>
 *     <input hxInput [hxDatepicker]="picker">
 *     <hx-datepicker-toggle hxIconSuffix [for]="picker"></hx-datepicker-toggle>
 *     <hx-datepicker #picker></hx-datepicker>
 *   </hx-form-group>
 *   ```;
 *
 * @example
 *   With custom icon:
 *   ```html
 *   <hx-datepicker-toggle hxIconSuffix [for]="picker">
 *   <hx-icon hxDatepickerToggleIcon svgIcon="calendar-check"></hx-icon>
 *   </hx-datepicker-toggle>
 *   ```
 */
@Component({
  selector: 'hx-datepicker-toggle',
  standalone: true,
  imports: [HxIconButton],
  templateUrl: './datepicker-toggle.html',
  styleUrl: './datepicker-toggle.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'hx-datepicker-toggle',
    '[attr.tabindex]': 'null',
    '[class.hx-datepicker-toggle-active]': 'isOpen()',
    // Bind click on host to prevent form field auto-focus (Angular Material pattern)
    '(click)': '_open($event)',
  },
  exportAs: 'hxDatepickerToggle',
})
export class HxDatepickerToggle extends HxBaseControl {
  /** Datepicker instance that the toggle button controls. */
  @Input('for')
  set datepicker(value: HxDatePicker | null) {
    if (value) {
      this._datepicker = value;
    }
  }
  get datepicker(): HxDatePicker | null {
    return this._datepicker;
  }
  private _datepicker: HxDatePicker | null = null;

  /** Whether the toggle button is disabled. Falls back to datepicker's disabled state if not explicitly set. */
  protected readonly _isDisabled = computed(() => {
    const localDisabled = this.disabledState();
    if (localDisabled) {
      return true;
    }
    return this.datepicker?.disabled() ?? false;
  });

  /** Tabindex for the toggle button. */
  override readonly tabIndex = input<number>(0);

  /**
   * Aria label for the toggle button.
   *
   * @default 'Open calendar'
   */
  override readonly ariaLabel = input<string | undefined>('Open calendar', { alias: 'aria-label' });

  /** Custom icon set by the consumer. */
  @ContentChild(HxDatepickerToggleIcon) _customIcon?: HxDatepickerToggleIcon;

  /** Underlying button element. */
  @ViewChild('button') _button?: ElementRef<HTMLButtonElement>;

  /** Opens the calendar when button is clicked. */
  _open(event: Event): void {
    if (this.datepicker && !this._isDisabled()) {
      this.datepicker.open();
      event.stopPropagation();
    }
  }

  /** Whether datepicker is currently open. */
  isOpen(): boolean {
    return this.datepicker?.isOpen?.() || false;
  }
}
